import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import MainProject from '@/model/MainProject'
import { parseProjectsBinFromBytes } from '@/features/project-disk/parseProjectsBin'
import type { NewProjectResolution } from '@/model/buildDefaultMainProject'
import { mergeFullProjectToDiskAndClearCache } from '@/features/project-disk/persistence'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type DataFileMap from '@/model/Resource/DataFileMap'
import {
  parseVirtualResourcesJson,
  serializeVirtualResources,
  writeVirtualResourcesToDataDir,
} from '@/features/virtual-resource/persistence'
import { normalizeProjectAudioEventPaths } from '@/features/virtual-resource/audioEventPaths'
import { syncVirtualResourcePathMetadata } from '@/features/virtual-resource/rules'
import { uploadVirtualResourcesJsonIfConfigured } from '@/control/virtualResourceCloud'
import { fetchCloudProjectIfConfigured } from '@/features/project-cloud/api'
import {
  type ProjectOssSaveProgress,
  type ProjectOssSaveResult,
} from '@/features/project-cloud/ossSave'
import { createAutoProjectArchive } from '@/features/project-archive/archiveService'
import {
  ensureProjectFontLoaded,
  normalizeProjectFontResources,
  type ProjectFontFallbackNotice,
} from '@/features/project-font/runtime'
import {
  createCloudWorkspaceProject,
  trackWorkspaceProjectCreate,
} from '@/features/workspace/usecases/projectCreation'
import {
  assertProjectHeaderIdMatches,
  createEmptyVirtualResourceRoots,
  markProjectBitmapFontRaw,
  normalizeVirtualResourceRoots,
  prepareProjectForStore,
  requireOpenedProject,
  type OpenedProjectMeta,
  type VirtualResourceRootsState,
} from '@/features/workspace/usecases/projectMount'
import {
  canSaveProjectToOss,
  saveWorkspaceProjectToOss,
  uploadWorkspaceVirtualResourcesToOss,
} from '@/features/workspace/usecases/projectSave'
import {
  createDefaultMallNew,
  readMallNewBin,
} from '@/features/project-mall/runtime'
import {
  openPublishedProjectVersion,
  type OpenPublishedProjectFontFallbackNotice,
  type OpenPublishedProjectProgress,
} from '@/features/published-project/openPublishedProject'
import type { PublishedProjectVersion3Item } from '@/features/published-project/publishedProjectHelpers'
import {
  generateWorkspaceBitmapFont,
  loadWorkspaceBitmapFontAssets,
} from '@/features/workspace/usecases/bitmapFont'
import { useProjectLoadingStore } from '@/store/projectLoading'
import { setEventDescriptionProject } from '@/control/eventDescriptionContext'
import { useAuthStore } from '@/features/auth'

/**
 * 等待一个最小的 UI 空档，让路由切换和大型对象挂载分成两个阶段执行，
 * 避免在同一事件循环里同时做过多工作导致界面卡顿。
 */
async function waitForUi(ms = 0): Promise<void> {
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/** 跳转到主工作区路由。 */
async function navigateToWorkspace(): Promise<void> {
  const { default: router } = await import('@/router')
  await router.push({ name: 'workspace' })
}

function revokePreviewOverrideUrl(url: string | null | undefined): void {
  if (!url || !url.startsWith('blob:')) return
  try {
    URL.revokeObjectURL(url)
  } catch {
    /* ignore invalid object url */
  }
}

function isCloudSessionExpiredError(error: unknown): boolean {
  const message = String(
    (error && typeof error === 'object'
      ? (error as { message?: unknown }).message
      : error) ?? '',
  ).toLowerCase()

  return [
    'invalid session',
    'session invalid',
    'session expired',
    '登录态已失效',
    '会话已失效',
    '会话失效',
    '请重新登录',
  ].some((keyword) => message.includes(keyword))
}

function getWorkspaceErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error || '未知错误')
}

function createCloudOpenStepError(step: string, error: unknown): Error {
  const message = getWorkspaceErrorMessage(error)
  return new Error(`失败步骤：${step}\n错误信息：${message}`)
}

async function runCloudOpenStep<T>(step: string, action: () => T | Promise<T>): Promise<T> {
  try {
    return await action()
  } catch (error) {
    throw createCloudOpenStepError(step, error)
  }
}

function loadProjectMallNewFromCloud(project: MainProject, bytes: Uint8Array | null): void {
  if (bytes && bytes.byteLength > 0) {
    readMallNewBin(bytes, project)
    return
  }
  createDefaultMallNew(project)
}

/** 将 OSS 保存阶段映射到创建工程流程的总进度区间。 */
function getCloudCreateSaveStageProgress(stage: ProjectOssSaveProgress['stage']): number {
  switch (stage) {
    case 'project-bin':
      return 78
    case 'mall-bin':
      return 82
    case 'virtual-resources':
      return 92
    case 'project-info':
      return 97
    default:
      return 72
  }
}

/**
 * 统一包装带加载条的异步流程。
 * 负责 begin / fail，调用方只需在合适时机手动 complete。
 */
async function runLoadingTask<T>(
  title: string,
  task: (loading: ReturnType<typeof useProjectLoadingStore>) => Promise<T>,
): Promise<T> {
  const loading = useProjectLoadingStore()
  loading.begin(title)
  try {
    return await task(loading)
  } catch (error) {
    loading.fail()
    throw error
  }
}

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    /**
     * 解析已完成、正在把 MainProject 挂入 store（大对象）时置为 true，
     * 用于主界面在 project 仍为 null 时显示「载入中」而非误显示「返回启动页」。
     */
    openingProject: false,
    project: null as MainProject | null,
    /** 当前 bin 文件名（多为 Data/projects.bin） */
    sourceFileName: null as string | null,
    /** 工程根目录名（浏览器无法提供完整磁盘路径） */
    rootLabel: null as string | null,
    markerFileName: null as string | null,
    rootDirectoryHandle: null as FileSystemDirectoryHandle | null,
    /** Data 目录（用于 Data/cache 分片） */
    dataDirectoryHandle: null as FileSystemDirectoryHandle | null,
    /** 云端工程打开时绑定的账号 uid，用于保存前阻断跨账号写入。 */
    openedOwnerUid: 0,
    isNew: false,
    /**
     * 各分类下的虚拟资源树根（URL 存在 DataFileMap 叶子 links 中）。
     * 非 null 时 ResourceBrowser 该分类优先走虚拟树而非本地目录。
     */
    virtualResourceRoots: {
      ...createEmptyVirtualResourceRoots(),
    } as VirtualResourceRootsState,
    /** 供资源管理器消费的“跳转到某分类/目录”提示。 */
    resourceBrowserReveal: {
      category: 'graphic',
      folderPath: '',
      token: 0,
    } as { category: ResourceCategoryKey; folderPath: string; token: number },
    /** 资源管理器触发的预览区资源预览。 */
    resourceCanvasPreview: {
      kind: 'none',
      url: '',
      name: '',
      resourceUrl: '',
      isSkinResource: false,
      token: 0,
    } as {
      kind: 'none' | 'image' | 'video' | 'oaf'
      url: string
      name: string
      resourceUrl: string
      isSkinResource: boolean
      token: number
    },
    /** 当前正在从资源管理器拖向 canvas 的资源元信息。 */
    canvasDragResource: null as null | {
      categoryKey: ResourceCategoryKey
      folderPath: string
      relativePath: string
      mappingPath: string
      resourceUrl: string
      name: string
      ext: string
      source?: 'native' | 'touch'
      token: number
    },
    /** 资源管理器的运行时预览覆盖：正式资源仍保存远端 URL，这里仅用于当前会话即时预览。 */
    resourcePreviewOverrides: {} as Record<string, string>,
    publishGindex: null as string | null,
    /** 工程内容存在未保存改动时为 true，用于刷新/关闭前提示。 */
    hasUnsavedChanges: false,
    virtualResourcesDirty: false,
    /** 顶栏作品名快照；`storyTree` 为 markRaw，需单独维护响应式标题。 */
    projectDisplayName: '',
  }),
  getters: {
    /** 生成窗口标题栏展示文本，兼容“载入中 / 新建工程 / 已打开工程”三种状态。 */
    displayTitle(state): string {
      if (state.openingProject && !state.project) {
        const hint = state.sourceFileName || state.rootLabel || '工程'
        return `载入中 · ${hint}`
      }
      if (!state.project) return ''
      const name = state.projectDisplayName
        || state.project.storyTree?.ProjectName?.trim()
      if (state.isNew) return name || '未命名工程'
      return name || state.rootLabel || state.sourceFileName || '工程'
    },
    /** 当前工程是否存在需要保存的改动。 */
    hasPendingChanges(state): boolean {
      return state.hasUnsavedChanges || state.virtualResourcesDirty
    },
  },
  actions: {
    /** 标记当前工程存在未保存内容改动。 */
    markProjectDirty(): void {
      if (!this.project) return
      this.hasUnsavedChanges = true
    },

    /** 标记当前工程已完成保存。 */
    markProjectSaved(): void {
      this.hasUnsavedChanges = false
    },

    /**
     * 从当前工程 `storyTree.ProjectName` 同步顶栏作品名。
     * @description `storyTree` 经 markRaw 挂载，直接改字段不会触发顶栏刷新。
     */
    syncProjectDisplayName() {
      const project = this.project as MainProject | null
      this.projectDisplayName = project?.storyTree?.ProjectName?.trim() ?? ''
    },

    /**
     * 将已解析完成的工程和来源元信息挂入 store。
     * 这是所有“云端打开 / 新建工程”最终汇合的统一入口。
     */
    applyOpenedProject(mp: MainProject, meta: OpenedProjectMeta) {
      this.project = mp
      setEventDescriptionProject(mp)
      this.syncProjectDisplayName()
      this.sourceFileName = meta.sourceFileName
      this.rootLabel = meta.rootLabel
      this.markerFileName = meta.markerFileName
      this.rootDirectoryHandle = meta.rootDirectoryHandle
      this.dataDirectoryHandle = meta.dataDirectoryHandle ?? null
      this.openedOwnerUid = meta.ownerUid ?? 0
      this.isNew = meta.isNew
      this.publishGindex = null
      this.hasUnsavedChanges = false
      this.virtualResourcesDirty = false
    },

    /**
     * 统一完成工程挂载前的预处理，并写入 store。
     * 入口方只需要关心来源元信息和可选的资源树，不必重复写 prepare/apply 细节。
     */
    mountOpenedProject(
      project: MainProject,
      meta: OpenedProjectMeta,
      options?: {
        virtualResourceRoots?: VirtualResourceRootsState
      },
    ): MainProject {
      const prepared = prepareProjectForStore(project)
      markProjectBitmapFontRaw(prepared)
      this.applyOpenedProject(prepared, meta)
      if (options?.virtualResourceRoots) {
        this.virtualResourceRoots = normalizeVirtualResourceRoots(options.virtualResourceRoots)
        this.virtualResourcesDirty = false
      }
      return prepared
    },

    /** 读取当前已打开工程；若工作区为空则直接抛错。 */
    getOpenedProject(): MainProject {
      return requireOpenedProject(this.getOpenedProjectOrNull())
    },

    /** 仅读取当前工程；允许为空，供“可选继续执行”的流程使用。 */
    getOpenedProjectOrNull(): MainProject | null {
      return this.project as MainProject | null
    },

    /**
     * 在挂载大工程对象前先写入轻量来源信息并进入工作区，
     * 避免启动页必须等待完整工程进入响应式后才切换。
     */
    async enterWorkspaceBeforeProjectMount(meta: OpenedProjectMeta): Promise<void> {
      this.sourceFileName = meta.sourceFileName
      this.rootLabel = meta.rootLabel
      this.markerFileName = meta.markerFileName
      await navigateToWorkspace()
      await nextTick()
    },

    /** 将所有虚拟资源根重置为空，恢复为仅依赖本地目录显示。 */
    resetVirtualResourceRoots() {
      this.virtualResourceRoots = createEmptyVirtualResourceRoots()
      this.virtualResourcesDirty = false
    },

    /** 替换某一分类的虚拟根（传 null 恢复为仅本地目录） */
    setVirtualResourceRoot(category: ResourceCategoryKey, root: DataFileMap | null) {
      this.virtualResourceRoots[category] = root ? syncVirtualResourcePathMetadata(category, root) : null
      this.markProjectDirty()
      this.virtualResourcesDirty = true
    },

    /** 批量替换虚拟资源树，并标记需要持久化。 */
    replaceVirtualResourceRoots(roots: VirtualResourceRootsState) {
      this.virtualResourceRoots = normalizeVirtualResourceRoots(roots)
      this.markProjectDirty()
      this.virtualResourcesDirty = true
    },

    /**
     * 按当前上下文持久化虚拟资源树。
     * 可选择写回本地 `Data/virtual_resources.json`，以及是否继续同步云端映射。
     */
    /** 将当前虚拟资源映射同步到云端（`virtual_resources` 接口 / OSS 保存链路）。 */
    async uploadVirtualResourcesToCloud(options?: { ignoreUploadError?: boolean }): Promise<void> {
      await this.persistVirtualResources({
        dataDirectoryHandle: this.dataDirectoryHandle,
        uploadToCloud: true,
        ignoreUploadError: options?.ignoreUploadError ?? false,
      })
    },

    async persistVirtualResources(options?: {
      dataDirectoryHandle?: FileSystemDirectoryHandle | null
      uploadToCloud?: boolean
      ignoreUploadError?: boolean
    }): Promise<void> {
      const dataDirectoryHandle = options?.dataDirectoryHandle ?? this.dataDirectoryHandle
      if (dataDirectoryHandle) {
        await writeVirtualResourcesToDataDir(dataDirectoryHandle, this.virtualResourceRoots)
      }
      if (!options?.uploadToCloud) {
        this.virtualResourcesDirty = false
        return
      }
      const project = this.getOpenedProjectOrNull()
      const auth = useAuthStore()
      if (project && canSaveProjectToOss(project, auth, this.openedOwnerUid)) {
        try {
          await uploadWorkspaceVirtualResourcesToOss({
            project,
            virtualResourceRoots: this.virtualResourceRoots,
            auth,
            ownerUid: this.openedOwnerUid,
          })
        } catch (error) {
          if (!options.ignoreUploadError) {
            throw error
          }
        }
      }
      try {
        await uploadVirtualResourcesJsonIfConfigured(this.virtualResourceRoots)
      } catch (error) {
        if (!options.ignoreUploadError) {
          throw error
        }
      }
      this.virtualResourcesDirty = false
    },

    async generateBitmapFont(extraText = '') {
      const project = this.getOpenedProjectOrNull()
      if (!project) return
      const { root: nextRoot } = await generateWorkspaceBitmapFont({
        project,
        currentRoot: this.virtualResourceRoots.font,
        extraText,
      })
      if (nextRoot) {
        this.virtualResourceRoots.font = nextRoot
      }
      markProjectBitmapFontRaw(project)
    },

    async loadBitmapFontAssets() {
      const project = this.getOpenedProjectOrNull()
      if (!project) return
      const { root: nextRoot } = await loadWorkspaceBitmapFontAssets({
        project,
        currentRoot: this.virtualResourceRoots.font,
      })
      if (nextRoot) {
        this.virtualResourceRoots.font = nextRoot
        this.virtualResourcesDirty = false
      }
      markProjectBitmapFontRaw(project)
    },

    /** 依据云端返回的 JSON 恢复虚拟资源树。 */
    applyCloudVirtualResourceRoots(virtualResourcesJson?: string | null): void {
      this.virtualResourceRoots = virtualResourcesJson
        ? normalizeVirtualResourceRoots(parseVirtualResourcesJson(virtualResourcesJson))
        : createEmptyVirtualResourceRoots()
      this.virtualResourcesDirty = false
    },

    async normalizeOpenedProjectFonts(options?: {
      uploadToCloud?: boolean
      ignoreUploadError?: boolean
      saveProjectToCloudOnFallback?: boolean
    }): Promise<ProjectFontFallbackNotice | null> {
      const project = this.getOpenedProjectOrNull()
      if (!project) return null
      const normalized = await normalizeProjectFontResources(project, this.virtualResourceRoots.font)
      this.virtualResourceRoots.font = normalized.root
      if (normalized.changed) {
        this.virtualResourcesDirty = true
        if (options?.saveProjectToCloudOnFallback && normalized.fallbackNotice) {
          await this.saveProjectToOss()
        } else if (options?.uploadToCloud) {
          await this.persistVirtualResources({
            uploadToCloud: true,
            ignoreUploadError: options.ignoreUploadError,
          })
        }
      }
      await ensureProjectFontLoaded(project, this.virtualResourceRoots.font)
      return normalized.fallbackNotice
    },

    /** 云端工程打开后，恢复虚拟资源、整理字体并清理运行时位图字体缓存。 */
    async finishOpeningCloudProject(virtualResourcesJson?: string | null): Promise<ProjectFontFallbackNotice | null> {
      await runCloudOpenStep('恢复虚拟资源映射', () => this.applyCloudVirtualResourceRoots(virtualResourcesJson))
      const fontFallbackNotice = await runCloudOpenStep('整理工程字体资源', () =>
        this.normalizeOpenedProjectFonts({
          uploadToCloud: true,
          ignoreUploadError: true,
          saveProjectToCloudOnFallback: true,
        }))
      await runCloudOpenStep('清理位图字体缓存', () => this.loadBitmapFontAssets())
      return fontFallbackNotice
    },

    /** 提示资源管理器切换到指定分类和子目录，便于应用素材后立即可见。 */
    revealResourceBrowserFolder(category: ResourceCategoryKey, folderSegments: string[] = []) {
      this.resourceBrowserReveal = {
        category,
        folderPath: folderSegments.filter(Boolean).join('/'),
        token: this.resourceBrowserReveal.token + 1,
      }
    },

    /** 在主预览区显示资源管理器中选中的图像 / 视频资源。 */
    setResourceCanvasPreview(
      kind: 'image' | 'video' | 'oaf',
      url: string,
      name: string,
      resourceUrl = '',
      isSkinResource = false,
    ) {
      this.resourceCanvasPreview = {
        kind,
        url,
        name,
        resourceUrl,
        isSkinResource,
        token: this.resourceCanvasPreview.token + 1,
      }
    },

    /** 清空资源管理器对主预览区的临时占用。 */
    clearResourceCanvasPreview() {
      this.resourceCanvasPreview = {
        kind: 'none',
        url: '',
        name: '',
        resourceUrl: '',
        isSkinResource: false,
        token: this.resourceCanvasPreview.token + 1,
      }
    },

    /** 记录当前正在拖入预览区的资源，避免依赖浏览器拖拽阶段读取自定义 MIME。 */
    setCanvasDragResource(payload: {
      categoryKey: ResourceCategoryKey
      folderPath: string
      relativePath: string
      mappingPath: string
      resourceUrl: string
      name: string
      ext: string
      source?: 'native' | 'touch'
    }) {
      this.canvasDragResource = {
        ...payload,
        token: (this.canvasDragResource?.token ?? 0) + 1,
      }
    },

    /** 清空当前 canvas 拖拽资源元信息。 */
    clearCanvasDragResource() {
      this.canvasDragResource = null
    },

    /** 为指定资源 URL 设置当前会话内的本地预览覆盖。 */
    setResourcePreviewOverride(resourceUrl: string, previewUrl: string) {
      const key = resourceUrl.trim()
      const next = previewUrl.trim()
      if (!key || !next) return
      const current = this.resourcePreviewOverrides[key]
      if (current && current !== next) {
        revokePreviewOverrideUrl(current)
      }
      this.resourcePreviewOverrides = {
        ...this.resourcePreviewOverrides,
        [key]: next,
      }
    },

    /** 移除指定资源 URL 的本地预览覆盖。 */
    removeResourcePreviewOverride(resourceUrl: string) {
      const key = resourceUrl.trim()
      if (!key || !this.resourcePreviewOverrides[key]) return
      const current = this.resourcePreviewOverrides[key]
      revokePreviewOverrideUrl(current)
      const next = { ...this.resourcePreviewOverrides }
      delete next[key]
      this.resourcePreviewOverrides = next
    },

    /** 清空所有当前会话内的本地预览覆盖。 */
    clearResourcePreviewOverrides() {
      for (const url of Object.values(this.resourcePreviewOverrides)) {
        revokePreviewOverrideUrl(url)
      }
      this.resourcePreviewOverrides = {}
    },

    /**
     * 创建云端工程：先在内存中生成默认工程，再向远端申请 guid/create_time，
     * 最后把工程主包与虚拟资源初始化数据保存到 OSS。
     */
    async createNewCloudProject(
      gameName: string,
      resolution: NewProjectResolution,
      templateId: string,
    ): Promise<{ projectKey?: string }> {
      const auth = useAuthStore()
      return await runLoadingTask('创建工程…', async (pl) => {
        if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
          throw new Error('请先登录后再新建云端工程')
        }

        const runCreate = () => createCloudWorkspaceProject({
          gameName,
          resolution,
          templateId,
          uid: auth.uid,
          sessionId: auth.sessionId,
          onProgress: (percent, status) => {
            pl.set(percent, status)
          },
          onSaveProgress: (progress) => {
            pl.set(getCloudCreateSaveStageProgress(progress.stage), progress.detail || progress.fileName)
          },
        })

        let created
        try {
          created = await runCreate()
        } catch (error) {
          if (!isCloudSessionExpiredError(error)) {
            throw error
          }
          pl.set(12, '刷新登录态后重试创建工程…')
          if (!await auth.refreshSessionForRetry()) {
            throw new Error('登录态校验失败，请重新登录后再新建云端工程')
          }
          created = await runCreate()
        }
        const remoteMeta = created.remoteMeta
        const saveResult = created.saveResult
        const roots = created.roots
        const prepared = this.mountOpenedProject(created.project, {
          sourceFileName: null,
          rootLabel: gameName.trim() || null,
          markerFileName: null,
          rootDirectoryHandle: null,
          dataDirectoryHandle: null,
          ownerUid: auth.uid,
          isNew: false,
        }, {
          virtualResourceRoots: roots,
        })
        await this.normalizeOpenedProjectFonts()

        pl.set(99, '上报创建埋点…')
        trackWorkspaceProjectCreate({
          sessionId: auth.sessionId,
          uid: auth.uid,
          guid: remoteMeta.guid,
          project: prepared,
        })
        pl.complete()
        return { projectKey: saveResult.guid }
      })
    },

    /**
     * 云端打开：优先 HTTP 云端接口，缺失时回退到 OSS 主包读取。
     */
    async openCloudProject(projectKey: string): Promise<{ fontFallbackNotice: ProjectFontFallbackNotice | null }> {
      this.openingProject = true
      try {
        return await runLoadingTask('加载云端工程…', async (pl) => {
          const auth = useAuthStore()
          if (!auth.loggedIn || !auth.uid) {
            throw new Error('请先登录后再打开云端工程')
          }
          pl.set(8, '请求云端数据…')
          const cloud = await runCloudOpenStep('请求云端数据', () => fetchCloudProjectIfConfigured(projectKey))
          if (cloud.skipped) {
            await runCloudOpenStep('检查云端打开能力', () => {
              throw new Error('未配置云端打开能力，且当前 OSS 也不可用')
            })
            return { fontFallbackNotice: null }
          }

          pl.set(18, '解析工程数据…')
          const mp = await runCloudOpenStep('解析工程主包 projects.bin', () =>
            parseProjectsBinFromBytes(cloud.binBytes, (pct, msg) => {
              pl.set(18 + pct * 0.78, msg)
            }),
          )
          await runCloudOpenStep('校验工程 GUID', () => assertProjectHeaderIdMatches(mp, projectKey, '云端工程'))
          await runCloudOpenStep(
            '读取商城数据 MallNew.bin',
            () => loadProjectMallNewFromCloud(mp, cloud.mallNewBytes),
          )

          pl.set(99, '进入主界面…')
          await runCloudOpenStep('等待界面空档', () => waitForUi())
          await runCloudOpenStep('完成加载进度', () => pl.complete())
          const openedMeta: OpenedProjectMeta = {
            sourceFileName: 'cloud:projects.bin',
            rootLabel: cloud.projectName ?? projectKey,
            markerFileName: null,
            rootDirectoryHandle: null,
            dataDirectoryHandle: null,
            ownerUid: auth.uid,
            isNew: false,
          }
          await runCloudOpenStep('进入工作区路由', () => this.enterWorkspaceBeforeProjectMount(openedMeta))
          await runCloudOpenStep('挂载工程到工作区', () => this.mountOpenedProject(mp, openedMeta))
          await runCloudOpenStep('恢复发布上下文', () =>
            this.setPublishContext({
              publishGindex: cloud.publishGindex,
            }),
          )
          const fontFallbackNotice = await runCloudOpenStep('恢复云端资源与字体', () => this.finishOpeningCloudProject(cloud.virtualResourcesJson))
          return { fontFallbackNotice }
        })
      } finally {
        this.openingProject = false
      }
    },

    /** 从主站已发布版本打开服务器工程，并缓存发布必需的 bin 到 OSS。 */
    async openPublishedProject(params: {
      guid: string
      projectName: string
      version: PublishedProjectVersion3Item
      onProgress?: (progress: OpenPublishedProjectProgress) => void
    }): Promise<{ fontFallbackNotice: OpenPublishedProjectFontFallbackNotice | null }> {
      this.openingProject = true
      try {
        return await runLoadingTask('打开已发布作品…', async (pl) => {
          const auth = useAuthStore()
          if (!auth.loggedIn || !auth.sessionId || !auth.uid) {
            throw new Error('请先登录后再打开已发布作品')
          }
          const opened = await openPublishedProjectVersion({
            guid: params.guid,
            uid: auth.uid,
            sessionId: auth.sessionId,
            toolVersion: params.version.version,
            version: params.version,
            onProgress: (progress) => {
              params.onProgress?.(progress)
              pl.set(progress.percent, progress.message)
            },
          })
          assertProjectHeaderIdMatches(opened.project, params.guid, '已发布作品')
          const openedMeta: OpenedProjectMeta = {
            sourceFileName: 'published:projects.bin',
            rootLabel: params.projectName || params.guid,
            markerFileName: null,
            rootDirectoryHandle: null,
            dataDirectoryHandle: null,
            ownerUid: auth.uid,
            isNew: false,
          }
          await this.enterWorkspaceBeforeProjectMount(openedMeta)
          this.mountOpenedProject(opened.project, openedMeta, {
            virtualResourceRoots: opened.roots,
          })
          await this.normalizeOpenedProjectFonts()
          this.setPublishContext({
            publishGindex: params.version.gindex || null,
          })
          pl.complete()
          return {
            fontFallbackNotice: opened.fontFallbackNotice,
          }
        })
      } finally {
        this.openingProject = false
      }
    },

    /** 清空当前工作区上下文，恢复到未打开工程状态。 */
    clear() {
      this.openingProject = false
      setEventDescriptionProject(null)
      this.project?.bitmapFont?.dispose()
      this.project = null
      this.projectDisplayName = ''
      this.sourceFileName = null
      this.rootLabel = null
      this.markerFileName = null
      this.rootDirectoryHandle = null
      this.dataDirectoryHandle = null
      this.openedOwnerUid = 0
      this.isNew = false
      this.publishGindex = null
      this.hasUnsavedChanges = false
      this.virtualResourcesDirty = false
      this.resourceBrowserReveal = {
        category: 'graphic',
        folderPath: '',
        token: 0,
      }
      this.resourceCanvasPreview = {
        kind: 'none',
        url: '',
        name: '',
        resourceUrl: '',
        isSkinResource: false,
        token: 0,
      }
      this.canvasDragResource = null
      this.clearResourcePreviewOverrides()
      this.resetVirtualResourceRoots()
    },

    /** 更新发布上下文，供发布面板和发布服务复用。 */
    setPublishContext(meta: { publishGindex?: string | null }) {
      if (meta.publishGindex !== undefined) {
        this.publishGindex = meta.publishGindex
      }
    },

    /** 从当前内存工程直接导出 `projects.bin` 内容。 */
    getProjectBinBytes(): Uint8Array {
      const project = this.getOpenedProject()
      normalizeProjectAudioEventPaths(project, this.virtualResourceRoots.audio)
      return project.writeBinBuffer()
    },

    /** 从当前内存工程直接导出运行时使用的 `Game.bin` 内容。 */
    getGameBinBytes(): Uint8Array {
      const project = this.getOpenedProject()
      const guid = project.projectHeader.ID?.trim() || ''
      if (!guid) {
        throw new Error('工程缺少 guid，无法生成 Game.bin')
      }
      normalizeProjectAudioEventPaths(project, this.virtualResourceRoots.audio)
      return project.writeGameBinBuffer(project.screenHash.width, project.screenHash.height, guid)
    },

    /** 将当前虚拟资源树序列化为与 `Data/virtual_resources.json` 相同格式的 JSON。 */
    getVirtualResourcesJson(): string {
      return serializeVirtualResources(this.virtualResourceRoots)
    },

    /** 将当前工程和虚拟资源映射保存到 OSS。 */
    async saveProjectToOss(
      onProgress?: (progress: ProjectOssSaveProgress) => void,
    ): Promise<ProjectOssSaveResult> {
      const project = this.getOpenedProject()
      const auth = useAuthStore()
      return await saveWorkspaceProjectToOss({
        project,
        virtualResourceRoots: this.virtualResourceRoots,
        auth,
        ownerUid: this.openedOwnerUid,
        publishGindex: this.publishGindex,
        onProgress,
      })
    },

    async saveProject(
      onProgress?: (progress: ProjectOssSaveProgress) => void,
    ): Promise<{ savedToDisk: boolean; savedToOss: boolean }> {
      const project = this.getOpenedProject()
      const auth = useAuthStore()
      if (this.openedOwnerUid && auth.uid !== this.openedOwnerUid) {
        throw new Error('当前登录账号与打开工程的账号不一致，请返回起始页后重新打开作品')
      }

      let savedToDisk = false
      let savedToOss = false

      if (this.rootDirectoryHandle) {
        await this.mergeFullProjectToDisk()
        savedToDisk = true
      }

      if (canSaveProjectToOss(project, auth, this.openedOwnerUid)) {
        await this.saveProjectToOss(onProgress)
        savedToOss = true
        const guid = project.projectHeader.ID?.trim()
        if (guid && auth.uid) {
          void createAutoProjectArchive({
            guid,
            uid: auth.uid,
          }).catch(() => {})
        }
      }

      if (!savedToDisk && !savedToOss) {
        throw new Error('当前工程没有可用的保存目标')
      }

      this.markProjectSaved()
      return { savedToDisk, savedToOss }
    },

    /** 发布/导出：合并完整 projects.bin 并清空 Data/cache */
    async mergeFullProjectToDisk() {
      const root = this.rootDirectoryHandle
      const project = this.getOpenedProject()
      if (!root) {
        throw new Error('未打开可写磁盘工程')
      }
      normalizeProjectAudioEventPaths(project, this.virtualResourceRoots.audio)
      await mergeFullProjectToDiskAndClearCache(root, project)
      if (this.virtualResourcesDirty) {
        await this.persistVirtualResources({
          uploadToCloud: true,
          ignoreUploadError: true,
        })
      }
    },
  },
})
